import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { MODULOS, NOMES_MODULOS } from "@vetbolso/contracts";
import { nomeEspecie } from "@vetbolso/core";
import type { z } from "zod";
import { api, ler } from "../api.ts";
import type { BuscaFichas } from "../buscas.ts";
import { Cabecalho, Carregando, Entrada, ErroCarga, Selecao, Selo, SeloStatus, STATUS_FICHA, Tabela, Vazio } from "../ui.tsx";

export const NOME_MODULO: Record<string, string> = {
  bulario: "Bulário",
  doses: "Doses",
  toxicos: "Tóxicos",
  produtos: "Produtos (MAPA)",
  plantas: "Plantas tóxicas",
  vacinas: "Vacinas",
  regras: "Regras e normas",
  formulas: "Calculadoras",
  referencia: "Sinais vitais",
  laboratorio: "Laboratório",
  questoes: "Questões",
  escalas: "Escalas de dor",
  cenarios: "Cenários de exemplo",
  emergencia: "Emergência",
  mdr1: "MDR1",
  receita: "Receita",
  campo: "Campo",
  meta: "Metadados",
  mercado: "Mercado (interno)",
  auditoria: "Como auditamos",
  lacunas: "Lacunas",
};

export function PaginaFichas() {
  const busca = useSearch({ strict: false }) as z.infer<typeof BuscaFichas>;
  const navegar = useNavigate({ from: "/fichas" });
  const { data, isLoading, error } = useQuery({
    queryKey: ["fichas", busca],
    queryFn: () =>
      ler(api.fichas.$get({ query: { modulo: busca.modulo as never, status: busca.status as never, q: busca.q || undefined } })),
  });
  const set = (k: string, v: string) => navegar({ search: (s) => ({ ...s, [k]: v || undefined }) });
  const lista = busca.modulo && MODULOS[busca.modulo as keyof typeof MODULOS]?.tipo === "lista";

  return (
    <>
      <Cabecalho
        titulo="Fichas"
        sub="Cada número do app vem de uma ficha aprovada por veterinário, com a fonte ao lado."
        acoes={
          lista ? (
            <Link
              to="/fichas/nova"
              search={{ modulo: busca.modulo! }}
              className="rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-on-primary"
            >
              Nova ficha de {NOME_MODULO[busca.modulo!]?.toLowerCase()}
            </Link>
          ) : null
        }
      />
      <div className="flex flex-wrap gap-2">
        <Selecao className="w-auto" value={busca.modulo ?? ""} onChange={(e) => set("modulo", e.target.value)} aria-label="Módulo">
          <option value="">Todos os módulos</option>
          {NOMES_MODULOS.map((m) => (
            <option key={m} value={m}>
              {NOME_MODULO[m]}
            </option>
          ))}
        </Selecao>
        <Selecao className="w-auto" value={busca.status ?? ""} onChange={(e) => set("status", e.target.value)} aria-label="Status">
          <option value="">Todos os status</option>
          {Object.entries(STATUS_FICHA).map(([s, i]) => (
            <option key={s} value={s}>
              {i.rotulo}
            </option>
          ))}
        </Selecao>
        <Entrada
          className="w-64"
          type="search"
          placeholder="Buscar por título ou id"
          defaultValue={busca.q}
          onKeyDown={(e) => e.key === "Enter" && set("q", e.currentTarget.value)}
          aria-label="Buscar"
        />
      </div>
      {error ? (
        <ErroCarga erro={error} />
      ) : isLoading ? (
        <Carregando />
      ) : !data?.length ? (
        <Vazio>Nenhuma ficha com esses filtros.</Vazio>
      ) : (
        <Tabela>
          <thead>
            <tr>
              <th>Ficha</th>
              <th>Módulo</th>
              <th>Espécies</th>
              <th>Status</th>
              <th>Auditoria</th>
              <th>Atualizada</th>
            </tr>
          </thead>
          <tbody>
            {data.map((f) => (
              <tr key={f.id} className="hover:bg-surface-2">
                <td className="max-w-md">
                  <Link to="/fichas/$id" params={{ id: f.id }} className="font-semibold text-ink hover:text-primary">
                    {f.titulo}
                  </Link>
                  <div className="font-mono text-xs text-muted">{f.id}</div>
                </td>
                <td className="whitespace-nowrap text-muted">{NOME_MODULO[f.modulo] ?? f.modulo}</td>
                <td className="text-xs text-muted">{f.especies.map(nomeEspecie).join(", ")}</td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    <SeloStatus status={f.status} />
                    {f.publicada && f.alterada && f.status !== "aprovada" ? <Selo tom="destaque">publicada difere</Selo> : null}
                  </div>
                </td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {f.erros ? <Selo tom="perigo">{f.erros} erro(s)</Selo> : <Selo tom="ok">ok</Selo>}
                    {f.pendentes ? <Selo tom="alerta">{f.pendentes} pendente(s)</Selo> : null}
                  </div>
                </td>
                <td className="whitespace-nowrap text-xs text-muted">{new Date(f.atualizadoEm).toLocaleDateString("pt-BR")}</td>
              </tr>
            ))}
          </tbody>
        </Tabela>
      )}
    </>
  );
}
